import { BenefitItem, CompareItem, LimitCopyItem } from './models/subscription.model';

/**
 * Subscription reference catalog — static content for S1–S5, verbatim from
 * subscription-screens.jsx. Labels are `reference_portal_subscription_*` i18n
 * keys (Arabic copy in translations.dictionary.ts).
 *
 * MVP 1b (handoff 07 «خويك Premium»): tools are FREE for everyone
 * (`KHOYEK_TOOLS_ACCESS_MODE=open_all`), so no Premium surface here sells them —
 * the tools row reads «الكل ({n})» for BOTH tiers, {n} = the `GET /tools` count.
 * Every tier value (messages, voice, memory, group sessions, priority) and the
 * plan price are read by the facade from the API; a key the API does not send
 * yet falls back to the handoff 07 value (GAP 01, low priority).
 */

/** S1 free-pitch — Premium benefits (no tools). The voice row quotes the server limits. */
export const SUBSCRIPTION_BENEFITS: readonly BenefitItem[] = [
  { icon: 'chat', titleKey: 'reference_portal_subscription_benefit_messages', subKey: 'reference_portal_subscription_benefit_messages_sub' },
  { icon: 'mic', titleKey: 'reference_portal_subscription_benefit_voice', subKey: 'reference_portal_subscription_benefit_voice_sub', limit: 'voice' },
  { icon: 'brain', titleKey: 'reference_portal_subscription_benefit_memory', subKey: 'reference_portal_subscription_benefit_memory_sub' },
  { icon: 'users', titleKey: 'reference_portal_subscription_benefit_groups', subKey: 'reference_portal_subscription_benefit_groups_sub' },
] as const;

/**
 * S1 free-pitch — the Free-vs-Premium table, mirroring the handoff 07 table row
 * for row. `policy` / `perk` cells read the tier policy (free: `GET /plans` →
 * `free_policy` or `GET /subscription`; premium: `GET /plans`); `spec` cells are fixed copy.
 */
export const SUBSCRIPTION_COMPARE: readonly CompareItem[] = [
  {
    featureKey: 'reference_portal_subscription_compare_messages',
    free: { kind: 'policy', limit: 'messages' },
    premium: { kind: 'policy', limit: 'messages', fallbackKey: 'reference_portal_subscription_val_unlimited' },
  },
  {
    featureKey: 'reference_portal_subscription_compare_voice',
    free: { kind: 'policy', limit: 'voice' },
    premium: { kind: 'policy', limit: 'voice' },
  },
  {
    featureKey: 'reference_portal_subscription_compare_memory',
    free: { kind: 'perk', perk: 'memory' },
    premium: { kind: 'perk', perk: 'memory' },
  },
  {
    featureKey: 'reference_portal_subscription_compare_groups',
    free: { kind: 'perk', perk: 'groups' },
    premium: { kind: 'perk', perk: 'groups' },
  },
  {
    featureKey: 'reference_portal_subscription_compare_priority',
    free: { kind: 'perk', perk: 'priority' },
    premium: { kind: 'perk', perk: 'priority' },
  },
  {
    featureKey: 'reference_portal_subscription_compare_tools',
    free: { kind: 'tools' },
    premium: { kind: 'tools' },
  },
  {
    featureKey: 'reference_portal_subscription_compare_everyday',
    free: { kind: 'spec', key: 'reference_portal_subscription_val_full' },
    premium: { kind: 'spec', key: 'reference_portal_subscription_val_full' },
  },
] as const;

/** "What Premium unlocks" condensed list (trial-active + premium-active) — no tools. */
export const SUBSCRIPTION_UNLOCKED: readonly LimitCopyItem[] = [
  { key: 'reference_portal_subscription_unlock_messages' },
  { key: 'reference_portal_subscription_unlock_voice', limit: 'voice' },
  { key: 'reference_portal_subscription_unlock_memory' },
] as const;

/** S4 cancel — what stays vs what becomes limited + survey reasons. */
export const SUBSCRIPTION_CANCEL_KEEPS: readonly string[] = [
  'reference_portal_subscription_cancel_keep_memory',
  'reference_portal_subscription_cancel_keep_mood',
  'reference_portal_subscription_cancel_keep_sessions',
] as const;
/** The messages / voice lines quote the FREE limits when the server reports them. */
export const SUBSCRIPTION_CANCEL_CHANGES: readonly LimitCopyItem[] = [
  { key: 'reference_portal_subscription_cancel_change_messages', limit: 'messages' },
  { key: 'reference_portal_subscription_cancel_change_voice', limit: 'voice' },
  { key: 'reference_portal_subscription_cancel_change_memory' },
] as const;
export const SUBSCRIPTION_CANCEL_REASONS: readonly string[] = [
  'reference_portal_subscription_reason_price',
  'reference_portal_subscription_reason_unused',
  'reference_portal_subscription_reason_noneed',
  'reference_portal_subscription_reason_missing',
  'reference_portal_subscription_reason_other',
] as const;

// NOTE — there is intentionally no trial-recap stat catalog here. The S5
// end-of-trial screen used to show hardcoded counts (127 chats / 45 min / 8
// tools) for EVERY user; no `/subscription` endpoint reports per-trial totals,
// so the screen now shows the real plan comparison instead of invented numbers.
